import { Modal, Input, type InputRef } from "antd";
import {
  EditOutlined,
  CloseOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useState, useEffect, useRef } from "react";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { useMenuModifiers } from "../../../../hooks/useMenu";
import type { Item, Modifier } from "../../../../api/menu";
import { getItemName, getTranslation } from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles, KbdChip } from "../modalPrimitives";
import { useViewport } from "../../../../hooks/useViewport";
import { AddonCheckbox } from "./AddonCheckbox";
import { AddonGroupsSection } from "./AddonGroupsSection";
import { QuantityStepper } from "./QuantityStepper";
import { useAddonSelection, type AddonSelection } from "./useAddonSelection";
import { useQuantityInput } from "./useQuantityInput";

/** Изборът на ред от поръчката — каквото модалът префилва и връща. */
export interface OrderLineChoice {
  quantity: number;
  modifiers: number[];
  addons: AddonSelection;
  comment: string;
}

interface EditModifiersModalProps {
  item: Item | null;
  open: boolean;
  /** Начално състояние при отваряне — редът в редакция (моливът, „Промени")
   *  или гласовият избор; без него модалът е чист (нов ред). */
  initial?: OrderLineChoice | null;
  /** Къде е фокусът при отваряне на десктоп: „comment" от молива в „Преглед
   *  на поръчката", „quantity" от „Промени". */
  focusField?: "comment" | "quantity";
  /** Нов ред, а не редакция („+" на продукт със задължителни добавки,
   *  повторна поръчка, гласът): извън десктоп фокусът при отваряне е в
   *  количеството, а Enter в него НЕ потвърждава — човекът още не е видял
   *  добавките под екранната клавиатура, а при повторна поръчка
   *  потвърждението праща направо в сметката. */
  isNewLine?: boolean;
  onClose: () => void;
  onSave: (data: OrderLineChoice) => void;
  /** i18n ключ за primary бутона (получава {{price}}) — по подразбиране
   *  „Запази"; новият ред подава „Добави · {{price}}" / „Поръчай · {{price}}". */
  confirmLabelKey?: string;
  /** Отваря „Инфо" за продукта — подава се само на телефон (там футърът
      с Инфо бутона е скрит) и рендира info chip в хедъра на модала. */
  onInfo?: () => void;
  /** Изчерпаните опции на добавките за ред от дневно меню — не се показват */
  hiddenAddonOptionIds?: ReadonlySet<number>;
  /** Най-голямата бройка за ред от дневно меню с избраните добавки
   *  (порциите на основното и на добавките) — без него няма таван */
  lunchMaxQuantityOf?: (addons: AddonSelection) => number;
  /** Колко остават от опция за ред от дневно меню (без самия ред) — „N бр" */
  lunchOptionRoomOf?: (optionId: number) => number;
  /** Името на дневното меню на реда — „Остават N порции за днес от „Дневно““ */
  lunchMenuName?: string;
}

/**
 * „Промени" — количество + добавки + модификатори + коментар на ред от
 * поръчката. Редакция на вече маркиран ред: моливът в „Преглед на поръчката"
 * и бутонът „Промени" (F9). Нов ред със същия модал (`confirmLabelKey`): „+"
 * на продукт със задължителни/препоръчителни добавки, повторна поръчка от
 * историята на масата, редът на гласова поръчка.
 * „Запази"/Enter е заключен, докато задължителните добавки не са избрани.
 */
export const EditModifiersModal = ({
  item,
  open,
  initial,
  focusField = "comment",
  isNewLine = false,
  onClose,
  onSave,
  confirmLabelKey = "common.save",
  onInfo,
  hiddenAddonOptionIds,
  lunchMaxQuantityOf,
  lunchOptionRoomOf,
  lunchMenuName,
}: EditModifiersModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const [selectedModifiers, setSelectedModifiers] = useState<number[]>([]);
  const [comment, setComment] = useState("");
  const {
    quantity,
    quantityInput,
    setQuantity,
    handleQuantityInputChange,
    decrement,
    increment,
  } = useQuantityInput();
  const addonSelection = useAddonSelection(item);
  const {
    selectedAddonItems,
    setSelectedAddonItems,
    allRequiredAddonsFulfilled,
    absoluteAddonsPrice,
    perProductAddonsPrice,
  } = addonSelection;
  // На десктоп полето по focusField получава фокуса щом модалът се отвори —
  // от прегледа човекът идва да пише коментар, от „Промени" — да смени
  // количеството. Фокусът е след анимацията на antd Modal (afterOpenChange),
  // иначе диалогът си го взема обратно. На телефон/таблет не — екранната
  // клавиатура би покрила добавките (освен isNewLine).
  const commentRef = useRef<TextAreaRef>(null);
  const quantityRef = useRef<InputRef>(null);

  const allModifiers = useMenuModifiers();
  const modifiers: Modifier[] = item
    ? allModifiers.filter((m) => item.modifierIds.includes(m.id))
    : [];

  // Дневно меню: не повече от останалите порции на основното и на избраните
  // добавки. При редакция на ред бройката му се пази, дори порциите да са
  // намалели след маркирането (друг терминал) — таванът спира само ръста.
  const keptQuantity = !isNewLine && initial ? initial.quantity : 0;
  const lunchRoomOf = (addons: AddonSelection) => lunchMaxQuantityOf?.(addons) ?? Infinity;
  const lunchRoom = lunchRoomOf(selectedAddonItems);
  const maxQuantity = Math.max(lunchRoom, keptQuantity);

  // Модалът се префилва при всяко отваряне (не пази старо състояние) — от
  // реда в редакция, от гласа, или чист за нов ред. Бройката — веднага в
  // тавана за добавките на реда (модалът не се демонтира и пази старата).
  useEffect(() => {
    if (!open) return;
    const openingMax = Math.max(lunchRoomOf(initial?.addons ?? {}), keptQuantity);
    setQuantity(Math.min(initial?.quantity ?? 1, openingMax >= 1 ? openingMax : Infinity));
    setSelectedAddonItems(initial?.addons ?? {});
    setSelectedModifiers(initial?.modifiers ?? []);
    setComment(initial?.comment ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Нова добавка, от която остават по-малко, сваля бройката до тях — само при
  // смяна на тавана, докато модалът е отворен (не в commit-а на отварянето,
  // където бройката е още от предишното отваряне)
  const lastMaxRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) {
      lastMaxRef.current = null;
      return;
    }
    const changed = lastMaxRef.current !== null && lastMaxRef.current !== maxQuantity;
    lastMaxRef.current = maxQuantity;
    if (changed && maxQuantity >= 1 && quantity > maxQuantity) setQuantity(maxQuantity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, maxQuantity]);
  const handleQuantityTyped = (value: string) => {
    const parsed = parseFloat(value.replace(",", "."));
    if (!isNaN(parsed) && parsed > maxQuantity) setQuantity(Math.max(0, maxQuantity));
    else handleQuantityInputChange(value);
  };

  // 0 (или празно поле) не се записва — редът се маха от степера, не оттук
  const canSave = quantity > 0 && quantity <= maxQuantity && allRequiredAddonsFulfilled;

  const totalPrice = (
    ((item?.price ?? 0) + perProductAddonsPrice) * quantity +
    absoluteAddonsPrice
  ).toFixed(2);

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      quantity,
      modifiers: selectedModifiers,
      addons: selectedAddonItems,
      comment: comment.trim(),
    });
    onClose();
  };

  // Keyboard shortcuts — Enter запазва, Esc затваря. Обработчикът се подменя
  // при всеки рендер през ref-а: слушателят на window остава един, а Enter
  // праща ТЕКУЩИТЕ модификатори/добавки/коментар — същото като бутона.
  const handleKeyDownRef = useRef<(e: KeyboardEvent) => void>(() => {});
  handleKeyDownRef.current = (e: KeyboardEvent) => {
    if (
      e.target instanceof HTMLElement &&
      (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
    ) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };
  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => handleKeyDownRef.current(e);
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isDesktop]);

  if (!item) return null;

  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);
  const width = isMobile ? undefined : isTablet ? 520 : 640;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={(opened) => {
        if (!opened || !isDesktop) return;
        if (focusField === "quantity") quantityRef.current?.focus({ cursor: "all" });
        else commentRef.current?.focus({ cursor: "end" });
      }}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={{
        ...modalStyles,
        content: { ...modalStyles.content, borderRadius: 20 },
      }}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
        {/* Хедър — икон-chip + име на продукта + единична цена + close chip */}
        <div
          className={`flex items-center gap-3 border-b border-pos-line2 ${
            isNarrow ? "px-4 py-3.5" : "px-[22px] py-4"
          }`}
        >
          <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[11px] bg-pos-ready-bg text-pos-accent">
            <EditOutlined className="text-[20px]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-lg font-extrabold leading-tight text-pos-ink">
              {itemName}
            </h2>
            <p className="m-0 mt-0.5 font-pos-mono text-[13px] text-pos-muted">
              {item.price.toFixed(2)} EUR
            </p>
          </div>
          {onInfo && (
            <button
              type="button"
              aria-label="info"
              onClick={onInfo}
              className="grid h-[38px] w-[38px] flex-shrink-0 cursor-pointer place-items-center rounded-[10px] border border-pos-line2 bg-pos-card text-pos-dim transition-colors hover:text-pos-ink"
            >
              <InfoCircleOutlined className="text-[15px]" />
            </button>
          )}
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            className="grid h-[38px] w-[38px] flex-shrink-0 cursor-pointer place-items-center rounded-[10px] border border-pos-line2 bg-pos-card text-pos-dim transition-colors hover:text-pos-ink"
          >
            <CloseOutlined className="text-[15px]" />
          </button>
        </div>

        <div
          className={`flex min-h-0 flex-col gap-[18px] overflow-y-auto ${
            isNarrow ? "p-4" : "px-6 py-5"
          }`}
        >
          <QuantityStepper
            quantity={quantity}
            quantityInput={quantityInput}
            onInputChange={handleQuantityTyped}
            onDecrement={decrement}
            onIncrement={() => (quantity + 1 <= maxQuantity ? increment() : undefined)}
            maxQuantity={maxQuantity}
            isMobile={isMobile}
            inputRef={quantityRef}
            autoFocus={isNewLine && !isDesktop}
            // Фокусът идва тук от „Промени" — Enter запазва (shortcut ⏎)
            onPressEnter={isNewLine ? undefined : handleSave}
          />

          {Number.isFinite(lunchRoom) && (
            <span className="-mt-2.5 text-center text-[12.5px] font-semibold text-pos-accent-ink">
              {lunchMenuName
                ? t("lunchMenu.remainingFromMenu", { count: lunchRoom, menu: lunchMenuName })
                : t("lunchMenu.remainingTitle", { count: lunchRoom })}
            </span>
          )}

          <AddonGroupsSection
            selection={addonSelection}
            hiddenOptionIds={hiddenAddonOptionIds}
            optionRoomOf={lunchOptionRoomOf}
            lineQuantity={quantity}
          />

          {/* Modifiers — редове с чекбокс, същият език като добавките */}
          {modifiers.length > 0 && (
            <div className="flex flex-col gap-2.5">
              <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
                {t("ordering.modifiers")}
              </span>
              <div className="flex flex-col gap-1.5">
                {modifiers.map((modifier) => {
                  const modifierName = getTranslation(
                    modifier.translations,
                    i18n.language,
                    modifier.slug,
                  );
                  const isSelected = selectedModifiers.includes(modifier.id);
                  return (
                    <button
                      type="button"
                      key={modifier.id}
                      onClick={() => {
                        setSelectedModifiers((prev) =>
                          prev.includes(modifier.id)
                            ? prev.filter((id) => id !== modifier.id)
                            : [...prev, modifier.id],
                        );
                      }}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-[11px] transition-colors ${
                        isSelected
                          ? "border-pos-accent bg-pos-marked"
                          : "border-pos-line3 bg-pos-card"
                      }`}
                    >
                      <AddonCheckbox checked={isSelected} />
                      <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-pos-ink">
                        {modifierName}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Comment */}
          <div className="flex flex-col gap-2.5">
            <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
              {t("ordering.itemComment")}
            </span>
            <Input.TextArea
              ref={commentRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("ordering.itemCommentPlaceholder")}
              // Enter в коментара тригерва „Запази" (shortcut ⏎), вместо нов
              // ред; Shift+Enter остава за нов ред.
              onPressEnter={(e) => {
                if (e.shiftKey) return;
                e.preventDefault();
                handleSave();
              }}
              autoSize={{ minRows: 1, maxRows: 5 }}
              maxLength={200}
              className="!resize-none !rounded-xl !border-pos-ctl !bg-pos-panel !px-[15px] !py-[13px] !text-[15px] !leading-normal !text-pos-ink placeholder:!text-pos-muted"
            />
          </div>
        </div>

        {/* Футър — ghost „Назад" + primary „Запази" (новият ред — „Добави ·
            {сума}" / „Поръчай · {сума}" през confirmLabelKey) */}
        <div
          className={`flex gap-2.5 border-t border-pos-line2 ${
            isNarrow ? "px-4 py-3" : "px-[22px] py-3.5"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className="relative h-[54px] flex-1 cursor-pointer rounded-[14px] border border-pos-ctl bg-pos-card text-[15px] font-semibold text-pos-ink2 transition-colors hover:bg-pos-panel"
          >
            {t("common.back")}
            {isDesktop && (
              <KbdChip shortcut="Esc" colorClass="text-pos-faint" />
            )}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="relative h-[54px] flex-[2] cursor-pointer rounded-[14px] bg-pos-accent text-base font-bold text-white transition-colors hover:bg-pos-accent-ink disabled:cursor-not-allowed disabled:opacity-45"
          >
            {t(confirmLabelKey, { price: totalPrice })}
            {isDesktop && (
              // ⏎ е Unicode глиф, който рендира по-дребно от латиница — бъмпваме
              // размера, за да съвпадне визуално с "Esc" на съседния бутон.
              <span className="absolute right-2 top-[3px] font-pos-mono text-[14px] font-semibold leading-none text-white/60">
                ⏎
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
